import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Navigation, Camera, Plus, Trash2, Send, Flag } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useOutbox, applyOutbox, sendOrQueue } from "@/lib/offline";
import { TechMaterials, materialsPayload, TechReturns, returnsPayload, TechDevices, devicesPayload } from "@/pages/tech/TechMaterials";
import { TechServices } from "@/pages/tech/TechServices";
import { TechQuickClose } from "@/pages/tech/TechQuickClose";
import { SerialScanButton } from "@/components/SerialScanner";
import { useData } from "@/context/DataContext";
import { addressLine, addressExtra, OUTCOMES, OUTCOME_LABEL, MEASURES, fmtDateTime } from "@/lib/format";
import { Field } from "@/components/Common";
import { StatusBadge, TypeBadge, SlaChip, PriorityBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Box = ({ title, children }) => (
  <section className="mb-4 border bg-white p-4">
    <div className="micro-label mb-3">{title}</div>
    {children}
  </section>
);

const EXTRA = [["gpon", "Gpon attestazione"], ["bretellaggio", "Bretellaggio"], ["olo", "OLO / Operatore"], ["cluster", "Cluster"], ["characterization", "Caratterizzazione"]];
const TechExtra = ({ d }) => EXTRA.filter(([k]) => d?.[k]).map(([k, l]) => <Field key={k} label={l} value={d[k]} mono />);

const BigInput = ({ label, value, onChange, testId, scan, ...rest }) => (
  <div>
    <Label className="micro-label">{label}</Label>
    <div className="mt-1.5 flex items-center gap-2">
      <Input className="h-12 text-base" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...rest} />
      {scan && <SerialScanButton onDetected={onChange} testId={`${testId}-scan`} title={`Scansiona ${label}`} />}
    </div>
  </div>
);

const Pick = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="micro-label">{label}</Label>
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5 h-12 text-base" data-testid={testId}><SelectValue placeholder="Seleziona" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const OrderInfo = ({ o }) => {
  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine(o.address))}`;
  return (
    <>
      <Box title="Cliente e indirizzo">
        <div className="text-lg font-semibold">{o.customer_name || "—"}</div>
        <div className="mt-1 flex items-start gap-1 text-sm"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />{addressLine(o.address)}</div>
        {addressExtra(o.address) && <div className="ml-5 text-sm text-muted-foreground">{addressExtra(o.address)}</div>}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button asChild variant="outline" className="h-12"><a href={maps} target="_blank" rel="noreferrer" data-testid="tech-navigate-button"><Navigation className="mr-2 h-4 w-4" />Naviga</a></Button>
          <Button asChild variant="outline" className="h-12" disabled={!o.phone}><a href={`tel:${o.phone}`} data-testid="tech-call-button"><Phone className="mr-2 h-4 w-4" />Chiama</a></Button>
        </div>
      </Box>
      <Box title={o.service_type === "ASSURANCE" ? "Dati guasto" : o.service_type === "CONSTRUCTION" ? "Dati lavori" : "Dati attivazione"}>
        {o.service_type !== "ASSURANCE" ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo" value={o.delivery?.activation_type} /><Field label="Tecnologia" value={o.technology} />
            <Field label="Codice ordine OLO" value={o.delivery?.network_code} mono /><Field label="ROE / PTE" value={o.delivery?.roe} mono />
            <Field label="PFS / Armadio" value={o.delivery?.port_splitter} mono /><Field label="PTO" value={o.delivery?.pto} mono />
            <Field label="POP" value={o.delivery?.pop} mono /><Field label="ID Risorsa" value={o.delivery?.resource_id} mono />
            <Field label="Ora appuntamento" value={o.delivery?.appointment_time} mono /><Field label="Anticipo / Posticipo" value={o.delivery?.time_flex} />
            <div className="col-span-2"><Field label="Materiale / Psw apparato" value={o.delivery?.equipment} mono /></div>
            <div className="col-span-2"><Field label="ID Building" value={o.delivery?.building_id} mono /></div>
            <TechExtra d={o.delivery} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Field label="Sintomo" value={o.assurance?.symptom} /></div><Field label="Apertura" value={fmtDateTime(o.assurance?.opened_at)} />
            <Field label="Scadenza SLA" value={fmtDateTime(o.sla_due_at)} mono />
            <Field label="ID Risorsa" value={o.delivery?.resource_id} mono /><Field label="POP" value={o.delivery?.pop} mono />
            <Field label="PFS / Armadio" value={o.delivery?.port_splitter} mono /><Field label="ROE / PTE" value={o.delivery?.roe} mono />
            <Field label="Ora appuntamento" value={o.delivery?.appointment_time} mono /><Field label="ID Building" value={o.delivery?.building_id} mono />
            <TechExtra d={o.delivery} />
          </div>
        )}
        {o.notes && <div className="mt-3"><Field label="Note" value={o.notes} /></div>}
      </Box>
    </>
  );
};

const QUEUED = "Salvato sul dispositivo: verrà inviato appena torna la connessione";

const Photos = ({ order, outbox, onUploaded }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    let queued = false;
    for (const file of files) {
      try {
        const r = await sendOrQueue(api, { type: "photo", order_id: order.id, order_code: order.external_order_id, at: new Date().toISOString(), payload: { file, name: file.name } }, outbox);
        queued = queued || r.queued;
      } catch (e) {
        toast.error(errMsg(e));
      }
    }
    setBusy(false);
    if (queued) toast.info(QUEUED);
    onUploaded();
  };
  const photos = (order.attachments || []).filter((a) => a.visit_id === order.visit?.id);
  const pending = useMemo(() => outbox.filter((x) => x.type === "photo" && x.order_id === order.id).map((x) => ({ id: x.id, url: URL.createObjectURL(x.payload.file), name: x.payload.name })), [outbox, order.id]);
  useEffect(() => () => pending.forEach((p) => URL.revokeObjectURL(p.url)), [pending]);
  const need = order.requirements?.min_photos || 0;
  return (
    <Box title={`Foto e allegati della visita (${photos.length + pending.length}${need ? ` / minimo ${need}` : ""})`}>
      <input ref={ref} type="file" accept="image/*,application/pdf" capture="environment" multiple className="hidden" data-testid="tech-photo-input"
        onChange={(e) => { if (e.target.files.length) upload([...e.target.files]); e.target.value = ""; }} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="tech-photo-button"
        className="flex h-28 w-full flex-col items-center justify-center border-2 border-dashed border-primary/40 bg-accent/40 text-primary transition-colors active:bg-accent">
        <Camera className="h-7 w-7" /><span className="mt-1 text-sm font-semibold">{busy ? "Caricamento…" : "Scatta o carica foto"}</span>
      </button>
      {photos.length + pending.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="tech-photo-grid">
          {photos.map((a) => a.mime?.startsWith("image/")
            ? <img key={a.id} src={fileUrl(a.id)} alt={a.filename} className="aspect-square w-full border object-cover" />
            : <div key={a.id} className="flex aspect-square items-center justify-center border bg-muted p-1 text-center text-[10px]">{a.filename}</div>)}
          {pending.map((p) => (
            <div key={`p${p.id}`} className="relative" data-testid="tech-photo-pending">
              <img src={p.url} alt={p.name} className="aspect-square w-full border border-dashed border-amber-500 object-cover opacity-80" />
              <span className="absolute bottom-1 left-1 bg-amber-500 px-1 text-[9px] font-bold uppercase text-white">In coda</span>
            </div>
          ))}
        </div>
      )}
    </Box>
  );
};

const ReportForm = ({ order, outbox, onSubmitted }) => {
  const { outcome_codes } = useData();
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const [measures, setMeasures] = useState({});
  const [details, setDetails] = useState({});
  const [materials, setMaterials] = useState([]);
  const [devices, setDevices] = useState([]);
  const [services, setServices] = useState([]);
  const [returns, setReturns] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const outcomes = OUTCOMES[order.service_type];
  const cls = outcomes.find((x) => x.value === outcome)?.cls;
  const reasons = outcome_codes.filter((c) => c.service_type === order.service_type && (!cls || c.internal_class === cls) && (!c.customer_id || c.customer_id === order.customer_id));
  const required = order.requirements?.required_measures || [];
  const isDel = order.service_type !== "ASSURANCE";
  const setM = (k) => (v) => setMeasures({ ...measures, [k]: v });
  const setD = (k) => (v) => setDetails({ ...details, [k]: v });

  const submit = async (quick) => {
    setBusy(true);
    try {
      const payload = { outcome, reason_code: reason, notes, measures, details, services, ...quick,
        materials: [...materialsPayload(materials), ...devicesPayload(devices)], returns: returnsPayload(returns) };
      const r = await sendOrQueue(api, { type: "report", order_id: order.id, order_code: order.external_order_id, at: new Date().toISOString(), payload }, outbox);
      if (r.queued) toast.info(QUEUED);
      else { toast.success("Rapportino inviato"); window.dispatchEvent(new Event("tech:notices")); }
      onSubmitted();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="tech-report-form">
      {!isDel && order.service_type === "ASSURANCE" && <TechQuickClose orderId={order.id} busy={busy} onConfirm={submit} />}
      <Box title="Esito intervento">
        <div className="grid grid-cols-3 gap-2">
          {outcomes.map((x) => (
            <button key={x.value} type="button" onClick={() => { setOutcome(x.value); setReason(""); }} data-testid={`tech-outcome-${x.value}`}
              className={`h-14 border-2 text-sm font-bold transition-colors ${outcome === x.value ? (x.cls === "POSITIVE" ? "border-emerald-600 bg-emerald-600 text-white" : x.cls === "NEGATIVE" ? "border-red-600 bg-red-600 text-white" : "border-violet-600 bg-violet-600 text-white") : "border-border bg-white"}`}>
              {x.label}
            </button>
          ))}
        </div>
        <div className="mt-3"><Pick label="Causale *" value={reason} onChange={setReason} testId="tech-reason-select" options={reasons.map((r) => ({ value: r.external_code, label: `${r.external_code} — ${r.description}` }))} /></div>
        <div className="mt-4"><TechServices orderId={order.id} value={services} onChange={setServices} /></div>
      </Box>
      <Box title={isDel ? "Misure e dati tecnici" : "Misure e dati tecnici (facoltativi)"}>
        <div className="space-y-3">
          {Object.entries(MEASURES[order.service_type]).map(([k, l]) => (
            <BigInput key={k} label={`${l}${required.includes(k) ? " *" : ""}`} value={measures[k]} onChange={setM(k)} testId={`tech-measure-${k}`} />
          ))}
          {order.service_type === "CONSTRUCTION" ? (
            <BigInput label="Lavori eseguiti" value={details.works_done} onChange={setD("works_done")} testId="tech-detail-works" />
          ) : isDel ? (
            <>
              <BigInput label="Seriale ONT / Modem" value={details.ont_serial} onChange={setD("ont_serial")} testId="tech-detail-ont" scan />
              <BigInput label="PTO installata" value={details.pto_installed} onChange={setD("pto_installed")} testId="tech-detail-pto" />
              <BigInput label="Porta utilizzata" value={details.port_used} onChange={setD("port_used")} testId="tech-detail-port" />
            </>
          ) : (
            <>
              <BigInput label="Tipo di guasto" value={details.fault_type} onChange={setD("fault_type")} testId="tech-detail-fault-type" />
              <BigInput label="Diagnosi" value={details.diagnosis} onChange={setD("diagnosis")} testId="tech-detail-diagnosis" />
              <Pick label="Punto di guasto" value={details.fault_point} onChange={setD("fault_point")} testId="tech-detail-fault-point" options={["Rete esterna", "Distribuzione", "Cliente"].map((v) => ({ value: v, label: v }))} />
              <Pick label="Causa" value={details.cause} onChange={setD("cause")} testId="tech-detail-cause" options={["Rottura fibra", "Connettore", "Apparato", "Giunto", "Altro"].map((v) => ({ value: v, label: v }))} />
              <BigInput label="Componente sostituito" value={details.replaced_component} onChange={setD("replaced_component")} testId="tech-detail-component" />
              <BigInput label="Riparazione eseguita" value={details.repair} onChange={setD("repair")} testId="tech-detail-repair" />
              <BigInput label="Test conclusivi" value={details.final_tests} onChange={setD("final_tests")} testId="tech-detail-tests" />
            </>
          )}
        </div>
      </Box>
      <div className="mb-4"><TechMaterials list={materials} setList={setMaterials} teamId={order.team_id} /></div>
      {order.service_type !== "CONSTRUCTION" && <div className="mb-4"><TechDevices list={devices} setList={setDevices} teamId={order.team_id} customerId={order.customer_id} optional={!isDel} /></div>}
      <div className="mb-4"><TechReturns list={returns} setList={setReturns} /></div>
      <Box title="Note">
        <Textarea className="min-h-24 text-base" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Descrizione attività, criticità riscontrate…" data-testid="tech-notes" />
      </Box>
      <Button onClick={() => submit()} disabled={busy || !outcome || !reason || !services.length} className="h-14 w-full text-base" data-testid="tech-submit-report-button">
        <Send className="mr-2 h-5 w-5" />{busy ? "Invio…" : "Chiudi intervento e invia rapportino"}
      </Button>
    </div>
  );
};

export default function TechOrder() {
  const { id } = useParams();
  const [server, setO] = useState(null);
  const outbox = useOutbox();
  const load = useCallback(() => api.get(`/tech/orders/${id}`).then((r) => setO(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load, outbox.length]);
  const o = applyOutbox(server, outbox);

  const arrive = async () => {
    try {
      const r = await sendOrQueue(api, { type: "arrive", order_id: id, order_code: o.external_order_id, at: new Date().toISOString() }, outbox);
      if (r.queued) toast.info(QUEUED);
      else toast.success("Arrivo registrato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!o) return <div className="text-sm text-muted-foreground">Caricamento…</div>;
  return (
    <div data-testid="tech-order-page">
      <Link to="/tecnico" className="mb-4 inline-flex h-10 items-center gap-1 text-sm text-muted-foreground" data-testid="tech-back-link"><ArrowLeft className="h-4 w-4" />Interventi</Link>
      <div className="mb-5">
        <TypeBadge type={o.service_type} />
        <h1 className="mt-1 font-mono text-3xl font-semibold" data-testid="tech-order-code">{o.external_order_id}</h1>
        <div className="mt-2 flex flex-wrap gap-1.5"><StatusBadge status={o.status} testId="tech-order-status" />{o.priority !== "ORDINARIA" && <PriorityBadge priority={o.priority} />}<SlaChip order={o} />{o.visits_count > 0 && <span className="font-mono text-xs text-muted-foreground">Visite precedenti: {o.visits_count - (o.visit ? 1 : 0)}</span>}</div>
      </div>
      <OrderInfo o={o} />
      {o.status === "PLANNED" && (
        <Button onClick={arrive} className="mb-4 h-14 w-full text-base" data-testid="tech-arrive-button"><Flag className="mr-2 h-5 w-5" />Sono arrivato sul posto</Button>
      )}
      {o.status === "IN_PROGRESS" && (
        <>
          <div className="mb-4 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-sm">Arrivo registrato alle {fmtDateTime(o.visit?.arrived_at)} · tentativo #{o.visit?.attempt_no}. La partenza viene registrata all'invio.</div>
          <Photos order={o} outbox={outbox} onUploaded={load} />
          <ReportForm order={o} outbox={outbox} onSubmitted={load} />
        </>
      )}
      {["COMPLETED", "TO_VALIDATE", "VALIDATED", "SUSPENDED"].includes(o.status) && o.pending?.some((x) => x.type === "report") && (
        <div className={`border-l-4 p-4 text-sm ${o.pending.find((x) => x.type === "report").status === "error" ? "border-red-600 bg-red-50" : "border-amber-500 bg-amber-50"}`} data-testid="tech-report-queued">
          {o.pending.find((x) => x.type === "report").status === "error"
            ? <>Rapportino non accettato: <strong>{String(o.pending.find((x) => x.type === "report").error)}</strong>. Usa "Dettagli" nella barra in alto per riprovare o scartare l'operazione.</>
            : <>Rapportino salvato sul dispositivo{o.outcome && <> — esito <strong>{OUTCOME_LABEL[o.outcome]}</strong></>}. Verrà inviato automaticamente appena torna la connessione.</>}
        </div>
      )}
      {["COMPLETED", "TO_VALIDATE", "VALIDATED", "SUSPENDED"].includes(o.status) && !o.pending?.some((x) => x.type === "report") && (
        <div className="border-l-4 border-emerald-600 bg-emerald-50 p-4 text-sm" data-testid="tech-report-done">
          Rapportino inviato{o.outcome && <> — esito <strong>{OUTCOME_LABEL[o.outcome]}</strong></>}. {o.status === "VALIDATED" ? "Produzione validata." : o.status === "SUSPENDED" ? "Intervento sospeso: verrà ripianificato." : "In attesa di validazione."}
        </div>
      )}
    </div>
  );
}
